import React from 'react';
import { motion } from 'framer-motion';
import { HiBriefcase, HiCalendar, HiOfficeBuilding } from 'react-icons/hi';

const experiences = [
  {
    title: 'Full-Stack Software Engineer & Local Ambassador at GitStart (YC S19)',
    company: 'GitStart | Community (YC S19)',
    meta: 'Full-time · Remote · San Francisco, California, United States',
    duration: 'February 2022 - December 2025',
    responsibilities: [
      'Delivered 500+ production pull requests across 20+ projects for U.S. startups, including Alaan.',
      'Built features for AlaanPay, including internationalization and real-time transaction capabilities used by thousands of users.',
      'Developed integrations with developer tools that saved 120+ engineers about 4 hours per week in productivity.',
      'Improved repository workflows and platform performance using Node.js microservices architecture.',
      'Supported developer onboarding, conducted technical interviews, and helped grow the global engineering community.',
      'Represented GitStart locally through ambassador work, developer outreach, and community engagement.',
    ],
  },
  {
    title: 'Trainer & Project Coordinator - Mobile Application Project (Seda)',
    company: 'CodeToInspire & UN Women',
    meta: 'Contract · Remote · Herat, Herat Province, Afghanistan',
    duration: 'September 2025 - October 2025',
    responsibilities: [
      'Led the development and training program behind the Seda mobile application, supporting women-led technology initiatives.',
      'Coordinated end-to-end project delivery using Agile methodologies across development, training, and stakeholder communication.',
      'Co-developed the application with Flutter and Firebase, contributing to architecture decisions and code reviews.',
      'Designed and delivered technical training for 45 women developers and presented the project during the UN Women 16 Days of Activism campaign.',
    ],
  },
  {
    title: 'Front-End Mentor',
    company: 'Code to Inspire',
    meta: 'Full-time · Remote · Dover, Delaware, United States',
    duration: 'March 2026 - Present',
    responsibilities: [
      'Led large-scale front-end and web fundamentals training programs for 180-200+ students per cohort, covering HTML, CSS, JavaScript, Bootstrap, WordPress, and Webflow tools.',
      'Delivered structured live lectures and hands-on sessions that helped students build strong web development and CMS foundations.',
      'Created slide decks and recorded learning materials for YouTube and classroom use to support blended learning.',
      'Designed and guided real-world portfolio projects aligned with industry expectations and job readiness.',
      'Reviewed assignments, mentored students individually, and strengthened debugging, Git, GitHub, and frontend best practices.',
    ],
  },
  {
    title: 'Front-End Mentor - 50 AWTL Fellowship Program',
    company: 'Aseel',
    meta: 'Full-time · Remote · Herat, Herat Province, Afghanistan',
    duration: 'March 2025 - March 2026',
    responsibilities: [
      'Selected as a Fellow in the AWTL (Afghan Women\'s Tech Leadership) Program and later transitioned into a lead mentorship role based on strong teaching and content creation performance.',
      'Delivered front-end training in HTML, CSS, JavaScript, Git, GitHub, and Bootstrap while helping students build real-world portfolio projects.',
      'Led two classes of about 320 students each, providing live instruction, coding support, and real-time debugging help.',
      'Designed and delivered a complete curriculum with structured lessons, slides, and technical documentation.',
      'Produced educational tutorials in Dari and Pashto and mentored students through projects, code reviews, and graduation readiness.',
    ],
  },
  {
    title: 'Audio, Voice & Localization Contributor',
    company: 'Code to Inspire',
    meta: 'Freelance · Remote · Dover, Delaware, United States',
    duration: 'July 2022 - September 2022',
    responsibilities: [
      'Recorded Pashto voice content for educational and awareness-based materials across CTI initiatives.',
      'Transcribed spoken content into structured written scripts with strong linguistic accuracy.',
      'Contributed to British Council-related Pashto content focused on women\'s health and migration awareness.',
      'Supported localization work for educational materials and the Antura and the Letters learning project.',
    ],
  },
  {
    title: 'Lecturer',
    company: 'Computer Science Faculty of Herat University',
    meta: 'Contract · Herat Province, Afghanistan',
    duration: 'May 2022 - August 2022',
    responsibilities: [
      'Taught web development fundamentals to undergraduate computer science students.',
      'Delivered lectures on PHP, HTML, CSS, and JavaScript to strengthen practical coding skills.',
      'Designed assignments and lab sessions that helped students build functional web applications.',
      'Mentored students through hands-on development work and debugging exercises.',
    ],
  },
  {
    title: 'Frontend Software Engineer',
    company: 'TutiaTech ICT Co.Ltd',
    meta: 'Part-time · Herat Province, Afghanistan',
    duration: 'September 2021 - December 2021',
    responsibilities: [
      'Developed responsive user interfaces with React, improving usability and performance for client web applications.',
      'Collaborated with remote teams using Git version control and agile workflows.',
      'Integrated APIs and optimized front-end components for cross-browser compatibility.',
    ],
  },
  {
    title: 'Database Developer',
    company: 'AFGAMESTAN',
    meta: 'Internship · Herat Province, Afghanistan',
    duration: 'July 2019 - December 2019',
    responsibilities: [
      'Developed an Employee Management System handling employee records and database operations.',
      'Implemented optimized CRUD operations and database queries to improve data retrieval efficiency.',
      'Collaborated on backend logic and system design while maintaining data integrity and version control.',
    ],
  },
];

const Experience = () => {
  return (
    <section
      id="experience"
      className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-charcoal-900 via-navy-900 to-charcoal-800 text-white relative overflow-hidden"
    >
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-0 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-beige-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-navy-400 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-10 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-beige-400 font-semibold text-xs sm:text-sm uppercase tracking-wider">My Journey</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-2 font-primary">
            Professional Experience
          </h2>
          <div className="w-16 sm:w-20 h-1 bg-beige-500 mx-auto mt-3 sm:mt-4"></div>
          <p className="mt-4 sm:mt-6 text-gray-300 max-w-2xl mx-auto text-sm sm:text-base px-4">
            A journey of growth, innovation, and impactful contributions across software development,
            education, and leadership.
          </p>
        </motion.div>

        {/* Experience Timeline */}
        <div className="max-w-5xl mx-auto">
          {experiences.map((exp, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative mb-8 sm:mb-10 md:mb-12 last:mb-0"
            >
              {/* Timeline Line */}
              {index !== experiences.length - 1 && (
                <div className="absolute left-6 top-14 sm:top-16 bottom-0 w-0.5 bg-beige-400/20 hidden md:block"></div>
              )}

              <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                {/* Timeline Dot */}
                <div className="hidden md:flex flex-col items-center flex-shrink-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-beige-400 to-beige-600 flex items-center justify-center shadow-lg">
                    <HiBriefcase className="text-navy-900 text-lg sm:text-xl" />
                  </div>
                </div>

                {/* Content Card */}
                <div className="flex-1 bg-charcoal-800/50 backdrop-blur-sm border border-beige-400/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 hover:border-beige-400/50 transition-all duration-300 group hover:shadow-xl hover:shadow-beige-400/10">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-3 sm:mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-beige-300 mb-2 font-primary group-hover:text-beige-200 transition-colors">
                        {exp.title}
                      </h3>
                      <div className="flex flex-col gap-1.5 sm:gap-2 text-gray-400">
                        <div className="flex items-start gap-2">
                          <HiOfficeBuilding className="text-beige-500 flex-shrink-0 mt-0.5 text-sm sm:text-base" />
                          <div className="space-y-1">
                            <span className="block text-xs sm:text-sm">{exp.company}</span>
                            {exp.meta && (
                              <span className="block text-[11px] sm:text-xs text-gray-500">{exp.meta}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <HiCalendar className="text-beige-500 flex-shrink-0 text-sm sm:text-base" />
                          <span className="text-xs sm:text-sm">{exp.duration}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-2 sm:space-y-3 text-gray-300">
                    {exp.responsibilities.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 sm:gap-3">
                        <span className="text-beige-400 mt-1 sm:mt-1.5 flex-shrink-0 text-sm">▸</span>
                        <span className="leading-relaxed text-xs sm:text-sm md:text-base">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
